export type Locale = "en" | "ru";

const en = {
  pageTitle: "River of Seasons · Dr. Lukina",
  pageDescription: "A living multiplayer fishing experience in the Lower Volga.",
  switchLanguage: "Переключить на русский",
  languageButton: "РУ",
  backToDrLukina: "Back to Dr. Lukina",
  guestName: "River Guest",
  eyebrow: "LOWER VOLGA · LIVE SEASONS",
  titleFirst: "River of",
  titleSecond: "Seasons",
  lede: "A living fishing world shaped by real time, weather, patience, and the people beside you.",
  seasonNow: "{season} now",
  enterGame: "Enter the fishing game",
  roomType: "Room type",
  publicRiver: "Public river",
  lukinFamily: "Lukin family",
  anglerName: "Your angler name",
  familyCode: "Family access code",
  familyCodePlaceholder: "Enter private code",
  enterRiver: "Enter the river",
  signIn: "Sign in without a password",
  privacyNote: "Public chat is filtered. Video is intentionally disabled at launch for privacy.",
  footer: "Inspired by Dr. Lyudmila Lukina’s “Fishing Camp Online” concept · Learn responsible fishing decisions",
  shortNameError: "Choose an angler name with at least 2 characters.",
  familyRoomError: "The family room could not be opened. Check the access code and try again.",
  watchLine: "Watch the line…",
  chooseTackle: "Choose your tackle and cast when ready.",
  leaveRoom: "Leave room",
  familyBend: "Lukin Family Bend",
  publicRiverbank: "Public Riverbank",
  liveMultiplayer: "Live multiplayer",
  practiceConnection: "Practice connection",
  astrakhan: "Astrakhan",
  riverConditions: "River conditions",
  currentConditions: "Current moderate · Visibility good",
  nearbyAnglers: "Nearby anglers",
  fishingThisBend: "{count} fishing this bend",
  yourSession: "YOUR SESSION",
  fishAndWeight: "{count} fish · {weight} kg",
  keptAndLimit: "Kept {weight} / 10 kg",
  levelLabel: "LEVEL {level}",
  levelOne: "Commercial-size keeper",
  levelTwo: "Four seasons",
  levelThree: "Guardian of the Volga",
  seasonJournal: "Season journal: {count}/4",
  tackle: "Tackle",
  floatRod: "Float rod",
  floatHint: "Worm · patient",
  donka: "Donka",
  donkaHint: "Oil cake · deep",
  lure: "Lure",
  lureHint: "Active · predator",
  waitForBite: "WAIT FOR THE BITE",
  castLine: "CAST LINE",
  reelNow: "REEL NOW!",
  decideCatch: "{weight} kg {fish}. Commercial minimum in this game: {minimum} kg. Keep or release?",
  keepFish: "KEEP",
  releaseFish: "RELEASE",
  sizeRuleNote: "Training rules: keep only commercial-size fish, release protected or undersized fish, and never exceed 10 kg kept. Not legal advice.",
  correctKeep: "Correct: commercial-size {fish} kept. +{points}",
  correctRelease: "Correct release: {reason}. +{points}",
  matchBonus: " Tackle + season match bonus awarded.",
  wrongDecision: "That choice breaks this round’s rule ({reason}). {points} points.",
  decisionRejected: "That online catch could not be recorded. Please cast again.",
  reasonProtected: "protected species",
  reasonUndersized: "below commercial size",
  reasonLimit: "the 10 kg limit would be exceeded",
  reasonCommercial: "commercial-size fish within the limit",
  recipeUnlocked: "BONUS RECIPE UNLOCKED",
  recipeTitle: "Volga-style baked fish",
  recipeBody: "Bake a legal kept fish with onion, dill, lemon, and a spoon of sour cream until opaque and tender. Never use protected species.",
  riverChat: "River chat",
  riverQuiet: "The river is quiet. Say hello.",
  chatPlaceholder: "Message the river…",
  chatMessage: "Chat message",
  send: "Send",
  leaderboard: "Leaderboard",
  firstCatch: "First catch sets the pace",
  todaysActivity: "Today’s activity",
  casts: "casts",
  catches: "catches",
  released: "kg released",
  bite: "BITE! The float is under — reel now!",
  slippedAway: "It slipped away. Read the current and try again.",
  tooSoon: "Too soon — patience matters on the Volga.",
  tooLate: "A fraction too late. The fish is gone.",
  rareReleased: "Rare {fish}: observed, logged, and released. +{points}",
  caughtReleased: "{weight} kg {fish} — released safely. +{points}",
  river: "River",
  playerLanded: "{name} landed {fish}.",
  playerLandedWeight: "{name} landed {weight} kg {fish}.",
  playerSighted: "{name} sighted and released {fish}.",
} as const;

type MessageKey = keyof typeof en;

const ru: Record<MessageKey, string> = {
  pageTitle: "Река времён года · Доктор Лукина",
  pageDescription: "Живая многопользовательская рыбалка на Нижней Волге.",
  switchLanguage: "Switch to English",
  languageButton: "EN",
  backToDrLukina: "Вернуться на сайт доктора Лукиной",
  guestName: "Гость с реки",
  eyebrow: "НИЖНЯЯ ВОЛГА · ЖИВЫЕ СЕЗОНЫ",
  titleFirst: "Река",
  titleSecond: "времён года",
  lede: "Живой мир рыбалки, где всё зависит от времени, погоды, терпения и людей рядом.",
  seasonNow: "Сейчас {season}",
  enterGame: "Войти в игру",
  roomType: "Выбор комнаты",
  publicRiver: "Общая река",
  lukinFamily: "Семья Лукиных",
  anglerName: "Имя рыболова",
  familyCode: "Код семейного доступа",
  familyCodePlaceholder: "Введите секретный код",
  enterRiver: "Начать рыбалку",
  signIn: "Войти без пароля",
  privacyNote: "Сообщения в общем чате фильтруются. Видео отключено для защиты личных данных.",
  footer: "По мотивам идеи доктора Людмилы Лукиной «Рыболовный лагерь онлайн» · Учимся ответственной рыбалке",
  shortNameError: "Введите имя рыболова — не менее 2 символов.",
  familyRoomError: "Не удалось открыть семейную комнату. Проверьте код доступа и попробуйте снова.",
  watchLine: "Следите за поплавком…",
  chooseTackle: "Выберите снасть и забрасывайте удочку.",
  leaveRoom: "Выйти из комнаты",
  familyBend: "Семейная заводь Лукиных",
  publicRiverbank: "Общий берег",
  liveMultiplayer: "Игра с другими рыболовами",
  practiceConnection: "Тренировочный режим",
  astrakhan: "Астрахань",
  riverConditions: "Условия на реке",
  currentConditions: "Умеренное течение · Хорошая видимость",
  nearbyAnglers: "Рыболовы рядом",
  fishingThisBend: "Рыболовов в этой заводи: {count}",
  yourSession: "ВАШ РЕЗУЛЬТАТ",
  fishAndWeight: "Рыб: {count} · {weight} кг",
  keptAndLimit: "Взято: {weight} из 10 кг",
  levelLabel: "УРОВЕНЬ {level}",
  levelOne: "Промысловый размер",
  levelTwo: "Четыре сезона",
  levelThree: "Хранитель Волги",
  seasonJournal: "Дневник сезонов: {count}/4",
  tackle: "Снасти",
  floatRod: "Поплавочная удочка",
  floatHint: "Червь · терпение",
  donka: "Донка",
  donkaHint: "Жмых · глубина",
  lure: "Спиннинг",
  lureHint: "Активно · хищник",
  waitForBite: "ЖДИТЕ ПОКЛЁВКИ",
  castLine: "ЗАБРОСИТЬ УДОЧКУ",
  reelNow: "ПОДСЕКАЙТЕ!",
  decideCatch: "{weight} кг, {fish}. Промысловый минимум в игре: {minimum} кг. Оставить или отпустить?",
  keepFish: "ОСТАВИТЬ",
  releaseFish: "ОТПУСТИТЬ",
  sizeRuleNote: "Учебные правила: оставляйте только рыбу промыслового размера, отпускайте охраняемую и мелкую рыбу, не превышайте лимит 10 кг. Это не юридическая консультация.",
  correctKeep: "Верно: {fish} промыслового размера оставлена. +{points}",
  correctRelease: "Верно отпущена: {reason}. +{points}",
  matchBonus: " Начислен бонус за совпадение снасти и сезона.",
  wrongDecision: "Выбор нарушает правило раунда ({reason}). {points} очков.",
  decisionRejected: "Не удалось записать онлайн-улов. Забросьте удочку ещё раз.",
  reasonProtected: "охраняемый вид",
  reasonUndersized: "меньше промыслового размера",
  reasonLimit: "будет превышен лимит 10 кг",
  reasonCommercial: "промысловый размер и лимит не превышен",
  recipeUnlocked: "ОТКРЫТ БОНУСНЫЙ РЕЦЕПТ",
  recipeTitle: "Рыба по-волжски в духовке",
  recipeBody: "Запеките разрешённую к вылову рыбу с луком, укропом, лимоном и ложкой сметаны до готовности. Охраняемые виды не используются.",
  riverChat: "Чат на реке",
  riverQuiet: "На реке тихо. Поздоровайтесь!",
  chatPlaceholder: "Написать сообщение…",
  chatMessage: "Сообщение в чате",
  send: "Отправить",
  leaderboard: "Таблица лидеров",
  firstCatch: "Первый улов задаст темп",
  todaysActivity: "Сегодня на реке",
  casts: "забросов",
  catches: "уловов",
  released: "кг отпущено",
  bite: "КЛЮЁТ! Поплавок ушёл под воду — подсекайте!",
  slippedAway: "Сорвалась. Учтите течение и попробуйте ещё раз.",
  tooSoon: "Слишком рано — на Волге важно терпение.",
  tooLate: "Немного поздно. Рыба ушла.",
  rareReleased: "Редкая встреча: {fish}. Рыба отмечена и отпущена. +{points}",
  caughtReleased: "{weight} кг, {fish} — бережно отпущена. +{points}",
  river: "Река",
  playerLanded: "{name}: улов — {fish}.",
  playerLandedWeight: "{name}: улов {weight} кг — {fish}.",
  playerSighted: "{name}: замечена и отпущена рыба — {fish}.",
};

const messages: Record<Locale, Record<MessageKey, string>> = { en, ru };

export function initialLocale(saved: string | null, browserLanguage: string): Locale {
  if (saved === "en" || saved === "ru") return saved;
  return browserLanguage.toLowerCase().startsWith("ru") ? "ru" : "en";
}

export function isDefaultGuestName(value: string | null) {
  return value === en.guestName || value === ru.guestName;
}

export function translate(locale: Locale, key: MessageKey, values: Record<string, string | number> = {}) {
  return messages[locale][key].replace(/\{(\w+)\}/g, (token, name: string) => (
    Object.prototype.hasOwnProperty.call(values, name) ? String(values[name]) : token
  ));
}

const seasons: Record<Locale, Record<string, string>> = {
  en: { Winter: "Winter", Spring: "Spring", Summer: "Summer", Autumn: "Autumn" },
  ru: { Winter: "зима", Spring: "весна", Summer: "лето", Autumn: "осень" },
};

const weather: Record<Locale, Record<string, string>> = {
  en: { "Clear current": "Clear current", Crosswind: "Crosswind", "Light rain": "Light rain", "Falling pressure": "Falling pressure", "Calm water": "Calm water" },
  ru: { "Clear current": "Чистое течение", Crosswind: "Боковой ветер", "Light rain": "Небольшой дождь", "Falling pressure": "Давление падает", "Calm water": "Спокойная вода" },
};

const fish: Record<Locale, Record<string, string>> = {
  en: { roach: "Vobla", bream: "Bream", perch: "Perch", zander: "Zander", carp: "Wild carp", pike: "Pike", catfish: "Wels catfish", sterlet: "Sterlet" },
  ru: { roach: "вобла", bream: "лещ", perch: "окунь", zander: "судак", carp: "сазан", pike: "щука", catfish: "сом", sterlet: "стерлядь" },
};

export function legacyCatchDetails(value: string) {
  const weighted = value.trim().match(/^(\d+(?:\.\d+)?)\s*kg\s+(.+)$/i);
  const sighting = value.trim().match(/^(.+?)(?:\s+—\s+conservation sighting(?:\s+sighting)?|\s+sighting)$/i);
  const label = (weighted?.[2] || sighting?.[1] || "").trim().toLowerCase();
  if (!label) return null;
  const match = Object.entries(fish.en).find(([, name]) => name.toLowerCase() === label);
  if (!match) return null;
  return { fishId: match[0], weightKg: weighted ? Number(weighted[1]) : null, sighting: Boolean(sighting) };
}

export const seasonName = (locale: Locale, value: string) => seasons[locale][value] || value;
export const weatherName = (locale: Locale, value: string) => weather[locale][value] || value;
export const fishName = (locale: Locale, id: string, fallback = id) => fish[locale][id] || fallback;
